import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { withViewport } from '@prisjakt/ui-viewport';
import {
  ScrollHandler,
  getScrollPositionFromElement,
} from 'src/components/scroll-handler';
import { NAV_HEIGHT_WITH_MARGIN } from './constants';
import { Tab } from './types';
import { AppState } from 'src/reducers';
import { Tabs } from './styles';

type OwnProps = {
  borderBottom?: boolean;
  defaultActiveTabId: string;
  tabs: Tab[];
  viewport: {
    top: number;
  };
  background?: boolean;
  headerIsVisible?: boolean;
  onClick: (tab: string) => void;
  shouldScrollOnClick?: boolean;
};

const StickyTabNavigation = ({
  borderBottom,
  defaultActiveTabId,
  tabs,
  viewport,
  background,
  headerIsVisible = true,
  shouldScrollOnClick = true,
  onClick,
}: OwnProps) => {
  const location = useLocation();
  const [_activeTab, _setActiveTab] = useState<string>(defaultActiveTabId);
  const [_scrollOngoing, _setScrollOngoing] = useState<boolean>(false);
  const activeTab = useRef(_activeTab);
  const scrollOngoing = useRef(_scrollOngoing);
  const tabsRef = useRef<HTMLElement | null>(null);
  const isMobileSidebarOpen = useSelector(
    (state: AppState) => state.areas.navigation.mobileMenu.open
  );
  const showSearchBar = useSelector(
    (state: AppState) => state.areas.header.showSearchBar
  );

  const isScrolledDown = viewport && viewport.top > NAV_HEIGHT_WITH_MARGIN;
  const isFrontpage = location.pathname === '/';
  const isSearchPage = location.pathname.indexOf('/search') === 0;
  const sensitivity = 100;

  const setActiveTab = useCallback((data: string) => {
    activeTab.current = data;
    _setActiveTab(data);
  }, []);

  useEffect(() => {
    activeTab.current = defaultActiveTabId;
    setActiveTab(defaultActiveTabId);
  }, [defaultActiveTabId, setActiveTab]);

  const isSticky =
    tabsRef.current !== null &&
    window.scrollY >= getScrollPositionFromElement(tabsRef.current, 0);

  const setScrollOngoing = useCallback((data: boolean) => {
    scrollOngoing.current = data;
    _setScrollOngoing(data);
  }, []);

  const handleScrollOngoingState = useCallback(
    (element: MappedTab) => {
      if (
        activeTab.current === element.id &&
        window.scrollY >= element.position &&
        window.scrollY <= element.positionEnd
      )
        setScrollOngoing(false);
    },
    [setScrollOngoing]
  );

  const handleActiveStateOnClick = useCallback(
    (tab: string) => {
      setActiveTab(tab);
      if (shouldScrollOnClick) setScrollOngoing(true);
      if (onClick) onClick(tab);
    },
    [onClick, shouldScrollOnClick, setActiveTab, setScrollOngoing]
  );

  const handleActiveStateOnScroll = useCallback(() => {
    const tabElements: MappedTab[] = tabs
      .map(tab => {
        const element = document.getElementById(tab.id);
        const position = getScrollPositionFromElement(
          element,
          NAV_HEIGHT_WITH_MARGIN
        );

        return {
          element,
          position,
          id: tab.id,
        };
      })
      .map((element, index, array) => {
        return {
          ...element,
          positionEnd: array[index + 1]
            ? array[index + 1].position - 1
            : document.body.scrollHeight,
        };
      });

    tabElements.map(element => {
      if (!element.position) return null;

      // If there's an ongoing tab click scrollTo, do not change tab active state.
      if (scrollOngoing.current) {
        return handleScrollOngoingState(element);
      }
      // Sets active tab based on scroll position for manual scroll.
      if (
        activeTab.current !== element.id &&
        window.scrollY + sensitivity >= element.position
      ) {
        return setActiveTab(element.id);
      }
      // Set active tab to default if scroll position is lower than defaultActiveTab position
      if (
        activeTab.current !== element.id &&
        element.id === defaultActiveTabId &&
        window.scrollY < element.position
      ) {
        return setActiveTab(defaultActiveTabId);
      }
      return null;
    });
  }, [setActiveTab, handleScrollOngoingState, tabs, defaultActiveTabId]);

  const renderScrollHandler = () => {
    if (isSearchPage) {
      return null;
    }

    return (
      <>
        {shouldScrollOnClick && (
          <ScrollHandler
            onScrollCallback={handleActiveStateOnScroll}
            topMargin={NAV_HEIGHT_WITH_MARGIN}
          />
        )}
      </>
    );
  };

  const renderTabs = () => {
    const allTabs = tabs.map(tab => ({
      ...tab,
      active: tab.id === activeTab.current,
      to: `${location.pathname}${location.search}${tab.id}`,
    }));

    if (allTabs.length <= 2) {
      return null;
    }

    return (
      <Tabs
        ref={tabsRef}
        onClick={handleActiveStateOnClick}
        background={background}
        borderBottom={borderBottom}
        isSticky={background && isSticky}
        tabs={allTabs}
        mobileSearchBarVisible={
          (showSearchBar || isSearchPage || (isFrontpage && !isScrolledDown)) &&
          !isMobileSidebarOpen
        }
        headerIsVisible={headerIsVisible}
      />
    );
  };

  return (
    <>
      {renderScrollHandler()}
      {renderTabs()}
    </>
  );
};

export default withViewport(StickyTabNavigation);

interface MappedTab {
  positionEnd: number;
  element: HTMLElement | null;
  position: number;
  id: string;
}
